import { computed, inject, Service, signal } from "@angular/core";
import moment from "moment";
import { filter, finalize, map, Observable, of, Subscription, tap } from "rxjs";

import { Constants } from "../../../shared/constants/constants";
import { AdaaBoolean } from "../../../shared/constants/enums";
import { AttachFile, WorkflowDecisionModelType, WorkflowProcessCtl } from "../../../shared/models";
import { NotificationCommunication, SearchFields } from "../../../shared/models/notification-center-model";
import { NotificationCenterApiService } from "../../../shared/services/notification-center-service";
import { WorkflowsApiService } from "../../../shared/services/workflows-api-service";
import {
  buildNotificationDecision,
  buildNotificationSearchFields,
} from "../../notification-center/notification-center.utils";
import { Adaa4UnreadService } from "./adaa4-unread-service";

const PAGE_SIZE = 30;
const ACTION_NOTIFICATIONS = "A";

export type Adaa4ActionFilter = "create" | "update" | "delete" | "dataEntry";

const ACTION_TYPE_IDS: Record<Adaa4ActionFilter, number> = {
  create: Constants.NOTIFICATION_ACTION_TYPES.CREATE,
  update: Constants.NOTIFICATION_ACTION_TYPES.UPDATE,
  delete: Constants.NOTIFICATION_ACTION_TYPES.DELETE,
  dataEntry: Constants.NOTIFICATION_ACTION_TYPES.DATA_ENTRY,
};

@Service()
export class Adaa4NotificationsService {
  private readonly _notificationsApiService = inject(NotificationCenterApiService);
  private readonly _workflowsApiService = inject(WorkflowsApiService);
  private readonly _unreadService = inject(Adaa4UnreadService);

  search = signal("");
  action = signal<Adaa4ActionFilter | null>(null);

  items = signal<NotificationCommunication[]>([]);
  visibleItems = computed(() => {
    const action = this.action();
    if (!action) return this.items();

    return this.items().filter((item) => this.actionType(item) === action);
  });
  hasMore = signal(false);
  loading = signal(false);

  private _request?: Subscription;

  public reload(): void {
    this._request?.unsubscribe();
    this._request = this._fetch(0).subscribe((items) => this.items.set(items));
  }

  public loadMore(): void {
    this._request?.unsubscribe();
    this._request = this._fetch(this.items().length).subscribe((items) =>
      this.items.update((current) => [...current, ...items])
    );
  }

  public setSearch(term: string): void {
    if (term.includes("\\")) return;

    this.search.set(term);
    this.reload();
  }

  public setAction(action: Adaa4ActionFilter | null): void {
    this.action.set(action);
    this.reload();
  }

  public open(id: number): Observable<NotificationCommunication | undefined> {
    return this._notificationsApiService.markAsRead(id).pipe(
      map((res) => (res.inError ? undefined : res.responseData?.[0])),
      tap((notification) => {
        if (!notification) return;

        this.items.update((items) => items.map((item) => (item.id === id ? { ...item, isRead: AdaaBoolean.Y } : item)));
        this._unreadService.refresh();
      })
    );
  }

  public history(notification: NotificationCommunication): Observable<WorkflowProcessCtl[]> {
    if (notification.isActionPending !== AdaaBoolean.Y) return of([]);

    return this._workflowsApiService
      .getPreviousWfProcessCtlSteps(notification.wfProcessCtlId)
      .pipe(map((res) => (res.inError ? [] : (res.responseData ?? []))));
  }

  public decide(args: {
    notification: NotificationCommunication;
    approval: boolean;
    attachment: AttachFile | null;
    decision: Partial<WorkflowDecisionModelType> | undefined;
  }): Observable<boolean> {
    return this._workflowsApiService.submitWorkflowDecision(buildNotificationDecision(args)).pipe(
      filter((res) => !res.inError),
      tap(() => {
        this._unreadService.refresh();
        this.reload();
      }),
      map(() => true)
    );
  }

  public isPending(notification: NotificationCommunication): boolean {
    return notification.commType === "A" && notification.isActionPending === AdaaBoolean.Y;
  }

  public pendingDays(notification: NotificationCommunication): number {
    const today = moment().utcOffset(Constants.uaeTimezone).startOf("day");
    const created = moment(notification.createTS).utcOffset(Constants.uaeTimezone).startOf("day");
    return Math.max(0, today.diff(created, "days"));
  }

  public actionType(notification: NotificationCommunication): "create" | "update" | "delete" | "dataEntry" | "other" {
    const types = Constants.NOTIFICATION_ACTION_TYPES;

    switch (+notification.wfActionType) {
      case types.CREATE:
        return "create";
      case types.UPDATE:
        return "update";
      case types.DELETE:
        return "delete";
      case types.DATA_ENTRY:
        return "dataEntry";
      default:
        return "other";
    }
  }

  private _fetch(offset: number): Observable<NotificationCommunication[]> {
    this.loading.set(true);

    return this._notificationsApiService
      .getPage({
        offset,
        limit: PAGE_SIZE,
        type: ACTION_NOTIFICATIONS,
        searchFields: this._searchFields(),
      })
      .pipe(
        map((res) => (res.inError ? [] : (res.responseData ?? []))),
        tap((items) => this.hasMore.set(items.length === PAGE_SIZE)),
        finalize(() => this.loading.set(false))
      );
  }

  private _searchFields(): SearchFields | undefined {
    const search = this.search();
    const action = this.action();

    if (search) {
      return {
        ...buildNotificationSearchFields(search),
        entity_name_en: { value: search, order: null },
        entity_name_ae: { value: search, order: null },
      };
    }

    if (action) return { wf_action_type: { value: `${ACTION_TYPE_IDS[action]}`, order: null } };

    return undefined;
  }
}
